import React, { ReactNode, useCallback, useMemo } from "react";
import { Button, Divider, notification, Space } from "antd";
import type { NotificationArgsProps } from "antd";

type NotificationPlacement = NotificationArgsProps["placement"];

type OpenNotificationProps = (text: string, placement?: NotificationPlacement) => void;

/** Тост с заголовком и видом (ответ ♪ — `revoiceReply`): успех, предупреждение, ошибка. */
export interface ToastArgs {
    type: "success" | "info" | "warning" | "error";
    message: string;
    description?: string;
    /** Секунды; 0 — пока не закроют. */
    duration?: number;
    key?: string;
}

interface NotificationProviderProps {
    openNotification: OpenNotificationProps;
    openErrorNotification: OpenNotificationProps;
    openToast?: (toast: ToastArgs) => void;
}

const NotificationContext = React.createContext<NotificationProviderProps>(
    {} as NotificationProviderProps,
);

const NotificationProvider = ({ children }: { children: ReactNode }) => {
    const [api, contextHolder] = notification.useNotification();

    const openNotification = useCallback(
        (text: string, placement: NotificationPlacement = "bottomRight") => {
            api.info({
                message: `Notification`,
                description: (
                    <NotificationContext.Consumer>{() => text}</NotificationContext.Consumer>
                ),
                placement,
            });
        },
        [api],
    );

    const openErrorNotification = useCallback(
        (text: string, placement: NotificationPlacement = "bottomRight") => {
            api.error({
                message: `Error`,
                description: (
                    <NotificationContext.Consumer>{() => text}</NotificationContext.Consumer>
                ),
                placement,
            });
        },
        [api],
    );

    const openToast = useCallback(
        ({ type, message, description, duration, key }: ToastArgs) => {
            api[type]({
                message,
                description,
                placement: "bottomRight",
                ...(duration !== undefined ? { duration } : {}),
                ...(key ? { key } : {}),
            });
        },
        [api],
    );

    return (
        <NotificationContext.Provider value={{ openNotification, openErrorNotification, openToast }}>
            {contextHolder}
            {children}
        </NotificationContext.Provider>
    );
};

export { NotificationContext, NotificationProvider };
